import React, { Component } from "react";
import { Button, Modal, Table } from "react-bootstrap";
import { Helmet } from "react-helmet";
import axios from "axios";
import Layout from "../../Layout/layout";
import { apiHost } from "../../api/apiClient";
import "../../styles/admin/loginLimits.scss";

class LoginLimits extends Component {
  constructor(props) {
    super(props);
    this.state = {
      emailLimits: [],
      ipLimits: [],
      limitedUserCount: 0,
      loading: true,
      error: "",
      notice: "",
      clearTarget: null,
      clearing: false,
    };
  }

  componentDidMount() {
    this.loadLimits();
    this.refreshTimer = window.setInterval(this.loadLimits, 30000);
  }

  componentWillUnmount() {
    window.clearInterval(this.refreshTimer);
  }

  authHeaders = () => ({
    Authorization: "Bearer " + localStorage.getItem("token"),
  });

  loadLimits = async () => {
    try {
      const response = await axios.get(`${apiHost}/api/auth/LoginLimits`, {
        headers: this.authHeaders(),
      });
      this.setState({
        emailLimits: response.data.emailLimits || [],
        ipLimits: response.data.ipLimits || [],
        limitedUserCount: response.data.limitedUserCount || 0,
        loading: false,
        error: "",
      });
    } catch (error) {
      if (error.response && error.response.status == 401) {
        this.props.history.push("/login");
        return;
      }
      this.setState({ loading: false, error: "Login limits could not be loaded." });
    }
  };

  openClearModal = (target) => {
    this.setState({ clearTarget: target, notice: "" });
  };

  closeClearModal = () => {
    if (!this.state.clearing) this.setState({ clearTarget: null });
  };

  clearLimit = async () => {
    const { clearTarget } = this.state;
    if (!clearTarget) return;

    this.setState({ clearing: true });
    try {
      await axios.post(
        `${apiHost}/api/auth/ClearLoginThrottle`,
        clearTarget.type === "email"
          ? { Email: clearTarget.email }
          : { IpAddress: clearTarget.ipAddress },
        { headers: this.authHeaders() }
      );
      this.setState({
        clearTarget: null,
        clearing: false,
        notice: `${clearTarget.type === "email" ? "Email" : "IP"} login limit cleared.`,
      });
      await this.loadLimits();
      window.dispatchEvent(new Event("login-limits-changed"));
    } catch (error) {
      this.setState({ clearing: false, error: "The login limit could not be cleared." });
    }
  };

  formatExpiry = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleString();
  };

  render() {
    const {
      emailLimits,
      ipLimits,
      limitedUserCount,
      loading,
      error,
      notice,
      clearTarget,
      clearing,
    } = this.state;

    return (
      <Layout>
        <Helmet><title>Login Limits</title></Helmet>
        <div className="contentBody">
          <div className="contentWrapper login-limits-page">
            <div className="login-limits-header">
              <div>
                <h2>Login Limits</h2>
                <p>Review and clear temporary login restrictions caused by repeated failed attempts.</p>
              </div>
              <Button variant="outline-primary" onClick={this.loadLimits} disabled={loading}>
                <i className="fa fa-refresh"></i> Refresh
              </Button>
            </div>

            <div className="login-limits-summary">
              <div><strong>{limitedUserCount}</strong><span>Limited users</span></div>
              <div><strong>{ipLimits.length}</strong><span>Limited IP addresses</span></div>
            </div>

            {error && <div className="login-limits-alert error">{error}</div>}
            {notice && <div className="login-limits-alert success">{notice}</div>}

            <section className="login-limits-section">
              <div className="login-limits-section-title">
                <div><h3>User and email limits</h3><p>Ten failed attempts temporarily restrict an email for 15 minutes.</p></div>
                <span>{emailLimits.length}</span>
              </div>
              <Table responsive className="login-limits-table">
                <thead><tr><th>User</th><th>Email</th><th>Customer</th><th>Failures</th><th>Limited until</th><th></th></tr></thead>
                <tbody>
                  {!loading && emailLimits.length === 0 && (
                    <tr><td className="login-limits-empty" colSpan="6">No email login limits are active.</td></tr>
                  )}
                  {emailLimits.map((entry) => (
                    <tr key={entry.email}>
                      <td>{entry.userName || <span className="login-limits-unknown">Unrecognised address</span>}</td>
                      <td>{entry.email}</td>
                      <td>{entry.customerName || "-"}</td>
                      <td><strong>{entry.failedAttempts}</strong></td>
                      <td>{this.formatExpiry(entry.limitedUntilUtc)}</td>
                      <td><Button size="sm" variant="outline-danger" onClick={() => this.openClearModal({ type: "email", email: entry.email })}>Clear limit</Button></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </section>

            <section className="login-limits-section">
              <div className="login-limits-section-title">
                <div><h3>IP-wide limits</h3><p>These may affect several users sharing an office or network connection.</p></div>
                <span>{ipLimits.length}</span>
              </div>
              <Table responsive className="login-limits-table">
                <thead><tr><th>IP address</th><th>Failures</th><th>Limited until</th><th></th></tr></thead>
                <tbody>
                  {!loading && ipLimits.length === 0 && (
                    <tr><td className="login-limits-empty" colSpan="4">No IP-wide login limits are active.</td></tr>
                  )}
                  {ipLimits.map((entry) => (
                    <tr key={entry.ipAddress}>
                      <td><code>{entry.ipAddress}</code></td>
                      <td><strong>{entry.failedAttempts}</strong></td>
                      <td>{this.formatExpiry(entry.limitedUntilUtc)}</td>
                      <td><Button size="sm" variant="outline-danger" onClick={() => this.openClearModal({ type: "ip", ipAddress: entry.ipAddress })}>Clear limit</Button></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </section>
          </div>
        </div>

        <Modal centered show={!!clearTarget} onHide={this.closeClearModal}>
          <Modal.Header closeButton={!clearing}><Modal.Title>Clear Login Limit</Modal.Title></Modal.Header>
          <Modal.Body>
            Clear the temporary limit for <strong>{clearTarget ? clearTarget.email || clearTarget.ipAddress : ""}</strong>?
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" disabled={clearing} onClick={this.closeClearModal}>Cancel</Button>
            <Button variant="danger" disabled={clearing} onClick={this.clearLimit}>{clearing ? "Clearing..." : "Clear limit"}</Button>
          </Modal.Footer>
        </Modal>
      </Layout>
    );
  }
}

export default LoginLimits;
